import { autoinject, transient, computedFrom, observable, Disposable, BindingEngine, ViewEngine, PLATFORM, useView } from "aurelia-framework";
import { ChildComponent } from "../../../child-component";
import {
    NotificationClientService,
    NotificationType
} from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import {
    AddSubtaskRequest,
    RuleActionEnum,
    VetClientModel,
    VetRuleOutcomeStepEnum, VetStatusEnum,
    VetTaskClientModel,
    VettingCommentActionEnum,
    VettingType
} from "../../../../../../models/vetting/vetting.models";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import { LookupValue, LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { SubtasksService } from "../../../../../../services/subtasks/subtasks.service";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { DataType, QueryFilter, SourceType } from "../../../../../../models/queries/queries.models";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { DynamicColumnModel, EntityDynamicValue } from "../../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { UserSecurityInfoModel } from "../../../../../../models/authentication/token.models";
import { Assignment } from "../../../../../components/assignment/assignment";
import { Reflection } from "../../../../../../utilities/reflection";
import { CoachmarkClientService } from "../../../../../../services/coachmark/coachmark-client.service";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Messages } from "../../../../../../configuration/event-messages.constants";
import { EventAggregator } from "aurelia-event-aggregator";
import { CargoTypeCategoryEnum } from "../../../../../../models/vessels/vessel.models";
import * as moment from "moment";
import { CustomerService } from "../../../../../../services/modules/customer-client.service";
import { PscActivitiesColumns } from "../../../../../../models/pscs/psc.models";
import { RightshipInspectionActivitiesColumns } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { GhgVerificationActivitiesColumns } from "../../../../../../models/ghgs/ghg.models";
import { IncidentActivitiesColumns } from "../../../../../../models/incidents/incident.models";
import { AutoScreeningService } from "../../../../../../services/vetting/auto-screening.service";
import { RuleDisplayResultSection } from "../../../../../../models/rules/rules.models";
import { RuleDisplayResultHelper } from "../../../../../../helpers/rule-display-result-helper";

@autoinject
@transient()
@useView('views/pages/modules/vetting/steps/vet-criteria/vet-criteria.html')
export class VetCriteria extends ChildComponent<VetClientModel> {

    thirdPartyDataSources: LookupValue[];
    thirdPartyDataTypes: LookupValue[];
    ocimfDataTypes: LookupValue[];
    vetCategories: LookupValue[];
    currentUserId: number;
    subtaskStatuses: LookupValue[];
    canEditVetCriteria: boolean;
    canAddVetTaskManually: boolean;
    canAddVetRfi: boolean;

    changeStatusToAwaitingResponse: boolean = true;
    assignToMe: any;
    canAssignToMe: any;
    viewCorrespondence: any;
    isRightShipUser: boolean;
    canContactVetRequestor: boolean;
    canAssignVetRequest: boolean;
    currentUser: UserSecurityInfoModel;
    passed: number = VetRuleOutcomeStepEnum.Pass;
    failed: number = VetRuleOutcomeStepEnum.Fail;
    na: number = VetRuleOutcomeStepEnum.NA;
    ruleDetailsEl: Element;
    assignment: Assignment;
    isRuleCollapsabileOpen: boolean = false;
    currentTask: VetTaskClientModel;
    datagridContent: CustomGridPageBase;
    public reloadItemFunc: any;

    public canProcessSanctionedVet: boolean;
    public canProcessVetUnknownPort: boolean;
    public canChangeShipManagerEmails: boolean;

    filter: string;
 
    @computedFrom(Reflection.getPropertyPath<VetCriteria>(z => z.item),
        Reflection.getPropertyPath<VetCriteria>(z => z.item.vettingTypeId),
        Reflection.getPropertyPath<VetCriteria>(z => z.canEditVetCriteria),
        Reflection.getPropertyPath<VetCriteria>(z => z.isClosed),
        Reflection.getPropertyPath<VetCriteria>(z => z.canProcessVet))
    get canReRunRules() {
        if (this.item && this.item.vettingTypeId == VettingType.AutoScreening) // always allow re-run rules
            return true;

        return this.canEditVetCriteria && !this.isClosed && this.canProcessVet;
    }

    @computedFrom(Reflection.getPropertyPath<VetCriteria>(z => z.item),
        Reflection.getPropertyPath<VetCriteria>(z => z.item.vetStatusId))
    get isClosed() {
        const final = [
            VetStatusEnum.ProcessingOutcome,
            VetStatusEnum.Cancelled,
            VetStatusEnum.Escalated,
            VetStatusEnum.Expired,
            VetStatusEnum.Accepted,
            VetStatusEnum.Completed,
            VetStatusEnum.Rejected,
            VetStatusEnum.AS_Passed,
            VetStatusEnum.AS_Failed,
            VetStatusEnum.AS_ExceptionRequired
        ];
        return this.item && (final.indexOf(this.item.vetStatusId) != -1);
    }
    
    @computedFrom(Reflection.getPropertyPath<VetCriteria>(z => z.item),
        Reflection.getPropertyPath<VetCriteria>(z => z.currentTask.vettingCommentAction),
        Reflection.getPropertyPath<VetCriteria>(z => z.currentTask.vetSuperVetTaskOutcomeId))
    get showIncludeComment() {
        if (this.currentTask) {
            if (this.currentTask.vettingCommentAction == VettingCommentActionEnum.NeverIncludeComment)
                return true;
            if (this.currentTask.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass && this.currentTask.vettingCommentAction == VettingCommentActionEnum.OnlyShowIfFailed)
                return true;
        }
        return false;
    }
    
    @computedFrom(Reflection.getPropertyPath<VetCriteria>(z => z.currentTask),
        Reflection.getPropertyPath<VetCriteria>(z => z.currentTask.vettingCommentAction))
    get vettingAction() {
        if (this.currentTask) {
            if (this.currentTask.vettingCommentAction == VettingCommentActionEnum.OnlyShowIfFailed) {
                return "Required by Customer: Only show if failed";
            }
            if (this.currentTask.vettingCommentAction == VettingCommentActionEnum.AlwaysIncludeComment) {
                return "Required by Customer: Always include comment";
            }
            if (this.currentTask.vettingCommentAction == VettingCommentActionEnum.NeverIncludeComment) {
                return "Required by Customer: Never include comment";
            }
        }
        return "";
    }

    getReadOnlyIf(vetTaskId: number) {
        return vetTaskId && !this.item.isMigratedVet ? this.customerService.getReadOnlyIf(vetTaskId) : "N/A";
    }

    constructor(notificationsService: NotificationClientService, i18N: I18N,
        private subtasksService: SubtasksService,
        private userClientService: UserClientService,
        private customerService: CustomerService,
        private autoScreeningService: AutoScreeningService,
        private vetService: VettingService,
        private cacheClient: CacheClientService,
        private userClaimsService: UserClaimsService,
        public notificationClientService: NotificationClientService,
        private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator,
        public coachmarkService: CoachmarkClientService,
        public viewEngine: ViewEngine,
        lookupClientService: LookupClientService, protected bindingEngine: BindingEngine
    ) {
        super(notificationsService, i18N);
        this.datagridContent = new CustomGridPageBase(this.i18N, "itemsName", this.bindingEngine);
        this.currentUser = userClientService.currentUser;
        this.canAddVetTaskManually = this.userClaimsService.hasPermission(Claims.CanAddVetTaskManually);
        this.canEditVetCriteria = this.userClaimsService.hasPermission(Claims.CanEditVetCriteria);
        this.canAssignVetRequest = this.userClaimsService.hasPermission(Claims.CanAssignVetRequest);
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAddVetRfi = this.userClaimsService.hasPermission(Claims.CanAddVetRfi);
        this.canContactVetRequestor = this.userClaimsService.hasPermission(Claims.CanContactVetRequestor);
        this.canChangeShipManagerEmails = this.userClaimsService.hasPermission(Claims.CanChangeShipManagerEmails);

        this.canProcessSanctionedVet = this.userClaimsService.hasPermission(Claims.CanProcessSanctionedVet);
        this.canProcessVetUnknownPort = this.userClaimsService.hasPermission(Claims.CanProcessVetUnknownPort);

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RuleCategories;
        lookupClientService.getLookup(request).then(x => {
            this.vetCategories = x.activeValues;
        });


        const request1 = new LookupValueRequest();
        request1.lookupValueQuery = LookupValueQuery.ThirdPartyDataSources;
        lookupClientService.getLookup(request1).then(x => {
            this.thirdPartyDataSources = x.activeValues;
        });

        const request2 = new LookupValueRequest();
        request2.lookupValueQuery = LookupValueQuery.ThirdPartyDataTypes;
        lookupClientService.getLookup(request2).then(x => {
            this.thirdPartyDataTypes = x.activeValues;
        });

        const request3 = new LookupValueRequest();
        request3.lookupValueQuery = LookupValueQuery.OcimfDataTypes;
        lookupClientService.getLookup(request3).then(x => {
            this.ocimfDataTypes = x.activeValues;
        });

        const requestSubtaskStatuses = new LookupValueRequest();
        requestSubtaskStatuses.lookupValueQuery = LookupValueQuery.SubtaskStatuses;
        lookupClientService.getLookup(requestSubtaskStatuses).then(x => {
            this.subtaskStatuses = x.activeValues;
        });

        this.currentUserId = this.userClientService.currentUser.id;
    }

    changeShipManagerEmails() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ChangeShipManagerEmail, {
            vet: this.item,
            onConfirm: (() => {
                if (this.reloadItemFunc)
                    this.reloadItemFunc();
            }).bind(this)
        }, true, false, false);
    }

    addTask() {

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddVetTaskDialog, {
                vet: this.item,
                onConfirm: (() => {
                    this.vetService.getVetTasks(this.item.vetId).then(x => {
                        this.item.vetTasks = x; 
                    });
                }).bind(this)
            }, true, false, false, "right");
        } else {
            if (this.canAssignToMe())
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: "Vet",
                    submit: this.assignToMe.bind(this),
                    callback: this.addTask.bind(this),
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
        }
    }

    async getThirdPartyPlugin(): Promise<string> {
        try {
            if (this.currentTask && this.currentTask.hasThirdParty && this.currentTask.ruleMeta && this.thirdPartyDataSources && this.thirdPartyDataTypes) {
                const json = JSON.parse(this.currentTask.ruleMeta);
                const dataSource = this.thirdPartyDataSources.filter(x => x.id == json.dataSourceId).map(x => x.additionalData["internalName"])[0];
                const dataType = this.thirdPartyDataTypes.filter(x => x.id == json.dataTypeId).map(x => x.additionalData["internalName"])[0];

                require(`views/pages/modules/vetting/steps/vet-criteria/additional-details/${dataSource}/${dataType}`);
                return PLATFORM.moduleName(`views/pages/modules/vetting/steps/vet-criteria/additional-details/${dataSource}/${dataType}`);
            }
            else if (this.currentTask && this.currentTask.ruleActionId == RuleActionEnum.OcimfPrefetch && this.currentTask.ruleMeta && this.ocimfDataTypes) {
                const json = JSON.parse(this.currentTask.ruleMeta);
                const dataType = this.ocimfDataTypes.filter(x => x.id == json.dataTypeId).map(x => x.additionalData["internalName"])[0];

                require(`views/pages/modules/vetting/steps/vet-criteria/additional-details/ocimf/${dataType}`);
                return PLATFORM.moduleName(`views/pages/modules/vetting/steps/vet-criteria/additional-details/ocimf/${dataType}`);
            }
        } catch (e) {
            console.error("Failed to load additional details: ", e);
        }

        return PLATFORM.moduleName(`views/pages/modules/vetting/steps/vet-criteria/additional-details/default/json-plugin`);
    }

    displayName(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.displayName;
    }

    isTextBox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Default ||
            column.tableColumnDataTypeId == DataType.Number ||
            column.tableColumnDataTypeId == DataType.Double;
    }

    getTextBoxType(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        if (column.tableColumnDataTypeId == DataType.Default) {
            return "text";
        }
        if (column.tableColumnDataTypeId == DataType.Number) {
            return "integer";
        }
        if (column.tableColumnDataTypeId == DataType.Double) {
            return "double";
        }
    }

    isCheckbox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Boolean;
    }

    isDate(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.DateTime;
    }

    async viewTask(task: VetTaskClientModel, shouldNotScroll: boolean = false) {
        this.isRuleCollapsabileOpen = false;
        this.currentTask = task;

        if (!task) return;

        if (this.currentTask.hasCap) {
            this.currentTask.capResponse = await this.vetService.getCapResponse(this.item.vetId, this.currentTask.vetTaskId);
        }
        else if (this.currentTask.hasPscInspections) {
            this.datagridContent.itemSource = {
                sourceType: SourceType.ODataUrl,
                source: "odata/VetTaskPscInspections",
                columns: PscActivitiesColumns.getColumns(true, this.isRightShipUser, !this.isRightShipUser, this.userClaimsService),
                initialFilters: [
                    QueryFilter.getQuery("vetTaskId", this.currentTask.vetTaskId, "eq", DataType.Number, true),
                    QueryFilter.getQuery("followUpInspection", true, "ne", DataType.Boolean, true),
                ]
            };
        } else if (this.currentTask.hasIncidents) {
            this.datagridContent.itemSource = {
                sourceType: SourceType.ODataUrl,
                source: "odata/VetTaskIncidents",
                columns: IncidentActivitiesColumns.getColumns(true, this.isRightShipUser, true, !this.isRightShipUser),
                initialFilters: [
                    QueryFilter.getQuery("vetTaskId", this.currentTask.vetTaskId, "eq", DataType.Number, true),
                ]
            };
        } else if (this.currentTask.hasInspections) {
            this.datagridContent.itemSource = {
                sourceType: SourceType.ODataUrl,
                source: "odata/VetTaskRightShipInspections",
                columns: RightshipInspectionActivitiesColumns.getColumns(true, true, false, { showSurvey: this.userClaimsService.hasPermission(Claims.CanViewSurveyColumnInTasks) }),
                initialFilters: [
                    QueryFilter.getQuery("vetTaskId", this.currentTask.vetTaskId, "eq", DataType.Number, true),
                ]
            };
        } else if (this.currentTask.hasCfrs) {
            const columns = [
                {
                    columnOrder: 1,
                    sortOrder: 1,
                    dataType: DataType.DateTime,
                    name: 'submittedDate',
                    title: 'Date Occurred',
                    isSortable: true,
                    valueConverter: (date: Date | string) => date
                        ? moment(date).format('DD MMM YYYY HH:mm')
                        : '',
                },
                {
                    columnOrder: 4,
                    sortOrder: 4,
                    dataType: DataType.CustomTemplate,
                    instancePerRow: true,
                    name: 'requesterCustomer',
                    title: 'Reporter/ CFR ID',
                    value:
                        '<div class="reviewer">' +
                        '<span class="name" if.bind="item.requester">${item.requesterCustomer} - ${item.requester}</span>' +
                        '<a class="link" href.bind="getItemRoute(item.cfrId)" target="_blank">${item && item.fbrItemId && item.fbrItemId > 0? \'FBR Outstanding Item\' : \'CFR\'} #${item.cfrId}</a>' +
                        '</div>',
                    isSortable: true,
                    parameter: {
                        item: null,
                        getItemRoute: this.getCfrItemRoute.bind(this)
                    }
                },
                {
                    columnOrder: 3,
                    sortOrder: 3,
                    dataType: DataType.Default,
                    name: 'cfrCategory',
                    title: 'Category',
                    isSortable: true
                },
                {
                    columnOrder: 4,
                    sortOrder: 4,
                    dataType: DataType.CustomTemplate,
                    instancePerRow: true,
                    name: 'status',
                    title: 'Status',
                    value:
                        '<div class="status-badge-value" if.bind="item.status">' +
                        '<div class="row nowrap align-items-middle">' +
                        '<div class="status-badge-dot"></div>' +
                        '<span>${item.status}</span>' +
                        '<div class="info" if.bind="item.awaitingResponseStatusReason && item.status.toLowerCase() != \'unacceptable\' && item.status.toLowerCase() != \'acceptable\'">' +
                        '<i class="icon-info material-icons">info_outline</i>' +

                        //Tooltip
                        '<div class="status-tooltip">' +
                        '<div class="wrap-tooltip">' +
                        '<p class="desc">${item.awaitingResponseStatusReason}</p>' +
                        '</div>' +
                        '</div>' +
                        '</div>' +
                        '</div>' +
                        '<span class="status-date">${item.statusDate | fromNow}</span>' +
                        '</div>',
                    isSortable: true,
                    getCellClass: (item: { status: string }) => {
                        return (item && item.status) ? `status-badge ${item.status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                    },
                    parameter: {
                        item: null,
                    }
                }, {
                    dataType: DataType.CustomTemplate,
                    value: '<div class="row align-items-middle">' +
                        '<comp-switch checked.two-way="item.vetApplicable" changed.delegate="vetApplicableChanged(item, $event.detail.value)"></comp-switch>' +
                        '</div>',
                    name: "vetApplicable",
                    title: "Vet Applicability",
                    instancePerRow: true,
                    sortOrder: 6,
                    columnOrder: 6,
                    isHidden: false,
                    isSortable: true,
                    parameter: {
                        item: null,
                        vetApplicableChanged: this.vetApplicableChanged.bind(this)
                    }
                }
            ];

            this.datagridContent.itemSource = (x) => {
                return {
                    sourceType: SourceType.ODataUrl,
                    source: "odata/VetTaskCfrs",
                    columns: columns,
                    initialFilters: [
                        QueryFilter.getQuery("vetTaskId", this.currentTask.vetTaskId, "eq", DataType.Number, true),
                        QueryFilter.getQuery("reportedAt", x, "eq", DataType.Number, true),
                    ]
                };
            };
        } else if (this.currentTask.hasGhgVerifications) {
            this.datagridContent.itemSource = {
                sourceType: SourceType.ODataUrl,
                source: "odata/VetTaskGhgVerifications",
                columns: GhgVerificationActivitiesColumns.getColumns(this.isRightShipUser),
                initialFilters: [
                    QueryFilter.getQuery("vetTaskId", this.currentTask.vetTaskId, "eq", DataType.Number, true),
                ]
            };
        } else {
            this.datagridContent.itemSource = null;
        }

        if (this.currentTask.hasForms && this.item.vettingTypeId != VettingType.AutoScreening) {
            this.currentTask.isReviewed = true;
            this.onPropertyChanged<VetCriteria>(x => x.item.vetTasks);
            this.subtasksService.setVetTaskAsReviewed(this.currentTask.vetTaskId);
        }

        if (this.currentTask.hasListItems && !this.currentTask.listItems) {
            this.currentTask.listItems = await this.vetService.getListItems(this.currentTask.vetTaskId);
        }

        if (this.currentTask.canViewDisplayResultsInCriteria) {
            const displayResultJson = await this.customerService.getDisplayResultJson(this.currentTask.vetTaskId);
            const displaySections: RuleDisplayResultSection[] = displayResultJson ? JSON.parse(displayResultJson).sections : [];
            displaySections.forEach(section => {
                section.items.forEach(item => {
                    item.text = RuleDisplayResultHelper.beatifyResultHtml(item, this.isRightShipUser);
                });
            });
            this.currentTask.displaySections = displaySections;
        }

        if (!shouldNotScroll)
            setTimeout(() => {
                if (this.currentTask && this.currentTask.element) {
                    this.currentTask.element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            }, 100);
    }

    @computedFrom("currentTask")
    get pendingPrefetch(){
        return this.currentTask && this.isPendingPrefetch(this.currentTask);
    }

    isPendingPrefetch(task: VetTaskClientModel) {
        return task.hasPendingPrefetchData && task.prefetchRuleActionId && !task.systemError;
    }

    public getCfrItemRoute = (id) => `/cfr/${id}?source=activities`;
    public vetApplicableChanged = async (item, vetApplicable) => {
        await this.vetService.changeVetApplicable(item.cfrId, item.vetTaskId, vetApplicable);
    };

    formLink(vesselApplicationFormId: number) {
        return `/vet-task-onboarding-custom-content?vesselApplicationFormId=${vesselApplicationFormId}`;
    }

    clearFilter() {
        this.filter = null;
    }

    getCount(outcomeId: number) {
        return this.item && this.item.vetTasks ? this.item.vetTasks.filter(x => x.vetSuperVetTaskOutcomeId == outcomeId && !x.isInfoOnlyRule).length : 0;
    }

    filterClick(outcomeId: number) {
        const index = this.selectedVetTaskStatusIds.indexOf(outcomeId);
        if (index >= 0)
            this.selectedVetTaskStatusIds.splice(index, 1);
        else
            this.selectedVetTaskStatusIds.push(outcomeId);
    }

    saveComments() {
        return new Promise<void>((resolve, reject) => {
            this.vetService.saveComments(this.item.vetId, [{
                vetTaskId: this.currentTask.vetTaskId,
                passedVettingComment: this.currentTask.passedVettingComment,
                failedVettingComment: this.currentTask.failedVettingComment,
                forceIncludeComment: this.currentTask.forceIncludeComment,
                infoVettingComment: this.currentTask.infoVettingComment,
                naVettingComment: this.currentTask.naVettingComment
            }], this.item.otherComments).then(() => {
                this.currentTask.vettingCommentDateModified = new Date();
                this.notificationClientService.show("Vetting Comment", "Vetting Comment saved successfully", NotificationType.Success);
                resolve();
            },  () => {
                this.notificationClientService.show("Vetting Comment", "Vetting Comment failed to save", NotificationType.Error);
                reject();
            });
        });
    }

    @computedFrom(Reflection.getPropertyPath<VetCriteria>(z => z.item),
        Reflection.getPropertyPath<VetCriteria>(z => z.item.vetTasks),
        Reflection.getPropertyPath<VetCriteria>(z => z.changes.item.vetTasks),
        Reflection.getPropertyPath<VetCriteria>(z => z.item.vetTasks.length))
    get anyNotReviewed() {
        if (!this.item || !this.item.vetTasks)
            return false;

        const tasks = this.item.vetTasks.filter(x => !x.isReviewed).length;
        return tasks > 0;
    }

    activate(model: { item: VetClientModel, assignment: Assignment }) {
        super.activate(model.item);

        this.assignment = model.assignment;
    }

    protected onActivated(model: VetClientModel) {
        super.onActivated(model);

        if (this.item) {
            this.loadRFIs();
        }

        const final = [
            VetStatusEnum.Cancelled,
            VetStatusEnum.Escalated,
            VetStatusEnum.Expired,
            VetStatusEnum.Completed,
            VetStatusEnum.Accepted,
            VetStatusEnum.Rejected,
            VetStatusEnum.AS_Passed,
            VetStatusEnum.AS_Failed,
            VetStatusEnum.AS_ExceptionRequired
        ];
        if (final.indexOf(model.vetStatusId) > -1) {
            this.selectedVetTaskStatusIds = [];
        }
    }

    @computedFrom(Reflection.getPropertyPath<VetCriteria>(v => v.item), Reflection.getPropertyPath<VetCriteria>(v => v.item.vetStatusId))
    public get statusIsFinal(): boolean {
        return this.item && (this.item.vetStatusId == VetStatusEnum.Escalated || this.item.vetStatusId == VetStatusEnum.Cancelled || this.item.vetStatusId == VetStatusEnum.Expired);
    }

    updateTaskOutcome(outcome: number, task: VetTaskClientModel = null, $event: Event) {
        $event?.stopPropagation();
        if (!this.canEditVetCriteria || this.isClosed || (this.isSanctionedCountry && !this.canProcessSanctionedVet) || (this.isUnknownCountry && !this.canProcessVetUnknownPort)) return;

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            const vetTaskId = task ? task.vetTaskId : this.currentTask.vetTaskId;
            const vetTaskOutComeId = task ? task.vetSuperVetTaskOutcomeId : this.currentTask.vetSuperVetTaskOutcomeId;
            const newOutcome = vetTaskOutComeId == outcome ? null : outcome;

            this.vetService.updateTaskOutcome(vetTaskId, newOutcome).then(result => {
                this.item.requireCustomerDecision = result.vetHasCustomerDecision;
                if(this.currentTask)
                {
                    this.currentTask.computedOutcome = false;

                    this.currentTask.vetSuperVetTaskOutcomeId = newOutcome;
                    this.currentTask.vetSuperLastModifiedDate = new Date();
                    this.currentTask.vetSuperLastModifiedName = this.userClientService.currentUser.userName;
                }

                this.onPropertyChanged<VetCriteria>(z => z.item.vetTasks)


            }, (result) => {
                if (result.validationResult && result.validationResult.hasError)
                    this.notificationClientService.show(this.i18N.tr('failed-to-update-outcome'), result.validationResult.errors[0].message, NotificationType.Error);
            });

        } else {
            if (this.canAssignToMe())
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: "Vet",
                    submit: this.assignToMe.bind(this),
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
        }
    }


    outcomes: LookupValue[] = [
        { name: "Review Required", id: null },
        { name: "Passed", id: 1 },
        { name: "Failed", id: 2 },
        { name: "N/A", id: 3 }
    ];

    @computedFrom("selectedVetTaskCategoryIds.length", "selectedVetTaskStatusIds.length", "item.vetTasks.length", "filter", "currentTask.vetSuperVetTaskOutcomeId")
    get vetTasks(): { [index: number]: VetTaskClientModel[] } {
        const tasks: { [index: number]: VetTaskClientModel[] } = {};
        if (this.item) {
            let vetTasks = this.item.vetTasks
            if (this.selectedVetTaskStatusIds && this.selectedVetTaskStatusIds.length > 0)
                vetTasks = vetTasks.filter(x => this.selectedVetTaskStatusIds.indexOf(x.vetSuperVetTaskOutcomeId) > -1);
            if (this.selectedVetTaskCategoryIds && this.selectedVetTaskCategoryIds.length > 0)
                vetTasks = vetTasks.filter(x => this.selectedVetTaskCategoryIds.indexOf(x.categoryId) > -1 || this.selectedVetTaskCategoryIds.indexOf(null) > -1);

            if (this.filter)
                vetTasks = 
                    vetTasks.filter(x => (x.sequenceNumber && x.sequenceNumber.toString().indexOf(this.filter.toLowerCase()) > -1) || 
                                         (x.title && x.title.toLowerCase().indexOf(this.filter.toLowerCase()) > -1) ||
                                         (x.ruleCode && x.ruleCode.toLowerCase().indexOf(this.filter.toLowerCase()) > -1));

            vetTasks = Enumerable.from(vetTasks).orderBy(x => x.sequenceNumber).toArray();

            for (const task of vetTasks) {
                const category = task.isManual ? 'Manual Tasks' : (task.category?.toLowerCase() ==  'rightship rules' || task.category?.toLowerCase().indexOf("baseline") > -1) && task.isOverriddenAtCustomer ? `${task.category} Modified to Customer Requirements` : task.category;
                if (tasks[category]) {
                    tasks[category].push(task);
                } else {
                    tasks[category] = [task];
                }
            }
        }

        return tasks;
    }

    applyOutcomeFilters(x: any) {
        this.selectedVetTaskStatusIds = x.filters.map(x => x.id);
        this.showVetTaskStatusFilter = false;
    }

    applyCategoryFilters(x: any) {
        this.selectedVetTaskCategoryIds = x.filters.map(x => x.id);
        this.showVetTaskCategoryFilter = false;
    }

    @computedFrom("selectedVetTaskCategoryIds.length", "selectedVetTaskStatusIds.length", "item.vetTasks.length", "filter")
    get vetTaskKeys() {
        return Enumerable.from(Object.keys(this.vetTasks)).orderBy(x => x).toArray();
    }

    closeRuleDetails() {
        this.currentTask = null;
        this.datagridContent.itemSource = null;
    }

    startCoachMark() {
        this.coachmarkService.start(RightShipQiRouterConstants.RouteNames.Vet);
    }

    toggle(element: HTMLElement, arrow: HTMLElement) {
        if (element) {
            $(element).slideToggle()
            if ($(arrow).hasClass('is-opened')) {
                $(arrow).removeClass('is-opened');
            } else {
                $(arrow).addClass('is-opened');
            }
        }
    }

    addSubtask() {
        this.addRFIs()
    }


    countRFIs: number = 0;
    rfis: { [index: number]: AddSubtaskRequest } = {};

    addRFIs() {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                entityId: this.item.vetId,
                entityTypeId: this.item.entityType,
                requestedBy: this.item.requestedBy,
                isWet: this.item.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet,
                emails: this.item.initialEmailsForCorrespondence,
                createSubtask: true,
                liaseWithShipManagers: this.item.liaseWithShipManagers,
                isPartialRFI: true,
                reload: (() => {
                    this.loadRFIs();
                    this.vetService.getVetTasks(this.item.vetId).then(x => {
                        this.item.vetTasks = x;
                    });
                }).bind(this)
            },
                true, false, false, 'right');
        } else {
            if (this.canAssignToMe())
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: "Vet",
                    submit: this.assignToMe.bind(this),
                    callback: this.addRFIs.bind(this),
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
        }

    }

    rfi(task: VetTaskClientModel) {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                entityId: this.item.vetId,
                entityTypeId: this.item.entityType,
                emails: this.item.initialEmailsForCorrespondence,
                linkedEntityId: task.vetTaskId,
                linkedEntityTypeId: EntityTypes.VetTask,
                isWet: this.item.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet,
                liaseWithShipManagers: this.item.liaseWithShipManagers,
                isPartialRFI: true,
                subtaskTypes: task.ruleSubtaskTypes,
                reload: (() => {
                    this.loadRFIs();
                    this.vetService.getVetTasks(this.item.vetId).then(x => {
                        this.item.vetTasks = x;
                    });
                }).bind(this)
            },
                true, false, false, 'right');
        } else {
            if (this.canAssignToMe())
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: "Vet",
                    submit: this.assignToMe.bind(this),
                    callback: this.rfi.bind(this, task),
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
        }
    }

    public loadRFIs() {
        this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-${this.item.vetId}`, CacheLocation.SessionStorage, false).then(x => {
            if (x) {
                this.countRFIs = Enumerable.from(x).where(x => x.value != null).count();
                this.rfis = x;
            } else {
                this.countRFIs = 0;
                this.rfis = {};
            }
        });
    }

    async rerunRules() {
        try {
            if (this.item) {
                if (this.item.vettingTypeId == VettingType.AutoScreening) {
                    await this.autoScreeningService.rerunRules(this.item.vetId)
                } else {
                    await this.vetService.rerunRules(this.item.vetId)
                }
                this.notificationClientService.show("Re-run Rules", "Successfully placed in queue", NotificationType.Success);
            }
        } catch (e) {
            this.notificationClientService.show("Re-run Rules", "Error", NotificationType.Error);
            throw e;
        }
    }

    @computedFrom("item", "item.portCountryOfLoadSanctioned", "item.portCountryOfDischargeSanctioned")
    get isSanctionedCountry(): boolean {
        return this.item.portCountryOfLoadSanctioned || this.item.portCountryOfDischargeSanctioned;
    }

    @computedFrom("item", "item.portCountryOfDischargeUnknown", "item.portCountryOfLoadUnknown")
    get isUnknownCountry(): boolean {
        return this.item.portCountryOfDischargeUnknown || this.item.portCountryOfLoadUnknown;
    }

    @computedFrom("isSanctionedCountry", "canProcessSanctionedVet", "isUnknownCountry", "canProcessVetUnknownPort")
    get canProcessVet() {
        return (!this.isSanctionedCountry || this.canProcessSanctionedVet) &&
            (!this.isUnknownCountry || this.canProcessVetUnknownPort)
    }


    @observable()
    public showVetTaskStatusFilter: boolean = false;
    public selectedVetTaskStatusIds: number[] = [null];

    public toggleVetTaskStatusFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showVetTaskStatusFilter) this.closeAllDropdowns();
        this.showVetTaskStatusFilter = !this.showVetTaskStatusFilter;
    }


    @observable()
    public showVetTaskCategoryFilter: boolean = false;
    public selectedVetTaskCategoryIds: number[] = [];

    public toggleVetTaskCategoryFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showVetTaskCategoryFilter) this.closeAllDropdowns();
        this.showVetTaskCategoryFilter = !this.showVetTaskCategoryFilter;
    }

    closeAllDropdowns() {
        this.showVetTaskStatusFilter = false;
        this.showVetTaskCategoryFilter = false;
    }

    _subscriptionToDocumentClickEvent: Disposable;

    attached() {
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeAllDropdowns.bind(this));
    }

    detached() {
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    @computedFrom("selectedVetTaskCategoryIds", "selectedVetTaskCategoryIds.length")
    public get vetTaskCategoryFilterActive(): boolean {
        return this.selectedVetTaskCategoryIds && this.selectedVetTaskCategoryIds.length > 0;
    }

    public clearVetTaskCategoryFilter() {
        this.selectedVetTaskCategoryIds = [];
    }

    @computedFrom("selectedVetTaskStatusIds", "selectedVetTaskStatusIds.length")
    public get vetTaskStatusFilterActive(): boolean {
        return this.selectedVetTaskStatusIds && this.selectedVetTaskStatusIds.length > 0;
    }

    public clearVetTaskStatusFilter() {
        this.selectedVetTaskStatusIds = [];
    }

    @computedFrom(Reflection.getPropertyPath<VetCriteria>(z => z.item.vetTasks),
    Reflection.getPropertyPath<VetCriteria>(z => z.changes.item.vetTasks),
    Reflection.getPropertyPath<VetCriteria>(z => z.item.vetTasks.length))
    get pendingRules() {
        return this.item.vetTasks.filter(task => !task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0).length;
    }

    @computedFrom("item", "item.initialEmailsForCorrespondence", "item.initialEmailsForCorrespondence.length")
    get shipManagerEmails() {
        if (this.item.initialEmailsForCorrespondence)
            return this.item.initialEmailsForCorrespondence.filter(x => x.isShipManagerEmail);
        return [];
    }

    contactVetRequestor() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.contactVetRequestorDialog, {
            entityId: this.item.vetId,
            emails: this.item.initialEmailsForCorrespondence.filter(x => !x.isShipManagerEmail),
            liaseWithShipManagers: this.item.liaseWithShipManagers,
            entityTypeId: this.item.entityType,
            requestedBy: this.item.requestedBy,
            reload: (() => {
                this.loadRFIs();
            }).bind(this)
        }, true, false, false, "right");
    }

    vetCriteriaContent: HTMLElement;

    @computedFrom("selectedVetTaskCategoryIds.length", "selectedVetTaskStatusIds.length", "item.vetTasks.length", "filter", "vetCriteriaContent", "currentTask")
    get paddingTop() {
        // PX-6635
        if (Enumerable.from(this.vetTasks).sum(x => x.value.length) > 4) {
            const task = Enumerable.from(this.vetTasks).selectMany<VetTaskClientModel>(x => x.value)
                .firstOrDefault(x => this.currentTask && this.currentTask.vetTaskId == x.vetTaskId);
            if (task && task.element && this.vetCriteriaContent)
                return task.element.offsetTop - this.vetCriteriaContent.offsetTop;
        }

        return 0;
    }
}
